/**
 * Budget Page (Add Expense)
 * Add new expense with category selection grid and expense history
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBudgetStore, type BudgetCategory } from '../store/budgetStore';
import { useUIStore } from '../store/uiStore';
import { transactionApi } from '../api/transactionApi';
import { useAuthStore } from '../store/authStore';
import { monthlyExpensesKeys } from '../hooks/useMonthlyExpenses';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { CurrencyButton } from '../components/ui/CurrencyButton';
import { type SupportedCurrency, CURRENCY_SYMBOLS } from '../types';
import {
  useExpenseEntries,
  useUpdateExpenseEntry,
  useDeleteExpenseEntry,
  type ExpenseEntryRecord,
} from '../hooks/useExpenseEntries';
import { EditExpenseModal } from '../components/ui/EditExpenseModal';
import { DeleteConfirmDialog } from '../components/ui/DeleteConfirmDialog';
import {
  CustoFixoIcon,
  LiberdadeIcon,
  ConfortoIcon,
  MetasIcon,
  PrazeresIcon,
  ConhecimentoIcon,
  HelpCircleIcon,
} from '../components/icons/CategoryIcons';
import { useCategoryName, getCategoryName } from '../components/ui/CategoryLabel';

// Month translation keys
const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
];

// Format number as Brazilian currency
const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Convert BRL amount back to original entry currency using stored snapshot
const getOriginalAmount = (
  amountBRL: number,
  currency: string,
  currencySnapshots: Record<string, number>
): number => {
  const rate = currencySnapshots?.[currency];
  if (!rate || rate === 0 || currency === 'BRL') {
    return amountBRL;
  }
  return amountBRL * rate;
};

// Format date for display
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Get icon component for category (supports both local and server IDs)
// Returns HelpCircle as fallback for unknown icons
const getCategoryIcon = (categoryId: string) => {
  const iconMap: Record<string, React.ReactNode> = {
    // Local IDs
    'custo-fixo': <CustoFixoIcon className="w-8 h-8" />,
    'liberdade': <LiberdadeIcon className="w-8 h-8" />,
    'conforto': <ConfortoIcon className="w-8 h-8" />,
    'metas': <MetasIcon className="w-8 h-8" />,
    'prazeres': <PrazeresIcon className="w-8 h-8" />,
    'conhecimento': <ConhecimentoIcon className="w-8 h-8" />,
    // Server IDs (nameKey from database)
    'fixed_costs': <CustoFixoIcon className="w-8 h-8" />,
    'financial_freedom': <LiberdadeIcon className="w-8 h-8" />,
    'comfort': <ConfortoIcon className="w-8 h-8" />,
    'goals': <MetasIcon className="w-8 h-8" />,
    'pleasures': <PrazeresIcon className="w-8 h-8" />,
    'knowledge': <ConhecimentoIcon className="w-8 h-8" />,
    // Fallback categories
    'fixed': <CustoFixoIcon className="w-8 h-8" />,
    'leisure': <PrazeresIcon className="w-8 h-8" />,
    'savings': <LiberdadeIcon className="w-8 h-8" />,
  };
  // Return HelpCircle icon as fallback for unknown category IDs
  return iconMap[categoryId] || <HelpCircleIcon className="w-8 h-8" />;
};

interface CategoryButtonProps {
  category: BudgetCategory;
  isSelected: boolean;
  onClick: () => void;
  isDark: boolean;
}

function CategoryButton({ category, isSelected, onClick, isDark }: CategoryButtonProps) {
  const categoryName = useCategoryName(category.id, category.name);
  return (
    <button
      onClick={onClick}
      data-testid={`category-button-${category.id}`}
      className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${
        isSelected
          ? 'border-primary-500 bg-primary-500/10'
          : isDark
            ? 'border-slate-700 bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800'
            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <div
        className={`mb-2 ${isSelected ? 'text-primary-500' : isDark ? 'text-slate-400' : 'text-slate-500'}`}
      >
        {getCategoryIcon(category.id)}
      </div>
      <span
        className={`text-xs font-medium text-center ${
          isSelected ? 'text-primary-500' : isDark ? 'text-slate-300' : 'text-slate-600'
        }`}
      >
        {categoryName.split(' ')[0]}
      </span>
    </button>
  );
}

// Action Menu Component
function ActionMenu({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  isDark,
}: {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDark: boolean;
}) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className={`absolute right-0 top-full mt-1 w-32 rounded-lg shadow-lg z-10 overflow-hidden ${
        isDark ? 'bg-slate-700 border border-slate-600' : 'bg-white border border-slate-200'
      }`}
      data-testid="expense-action-menu"
    >
      <button
        onClick={onEdit}
        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${
          isDark ? 'text-slate-200 hover:bg-slate-600' : 'text-slate-700 hover:bg-slate-100'
        }`}
        data-testid="expense-edit-action-button"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        {t('common.edit')}
      </button>
      <button
        onClick={onDelete}
        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${
          isDark ? 'text-red-400 hover:bg-slate-600' : 'text-red-600 hover:bg-red-50'
        }`}
        data-testid="expense-delete-action-button"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        {t('common.delete')}
      </button>
    </div>
  );
}

export function BudgetPage() {
  const { t } = useTranslation();
  const { categories, addExpense } = useBudgetStore();
  const { theme, addToast } = useUIStore();
  const { preferences } = useUserPreferences();
  const isDark = theme === 'dark';

  // Get translated month names
  const monthNames = MONTH_KEYS.map(key => t(`months.${key}`));
  const [amount, setAmount] = useState(0);
  const [displayAmount, setDisplayAmount] = useState('0,00');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Currency state - default to user's preferred currency
  const defaultCurrency = (preferences?.defaultCurrency as SupportedCurrency) || 'BRL';
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(defaultCurrency);

  // Update currency when preferences load
  useEffect(() => {
    if (preferences?.defaultCurrency) {
      setSelectedCurrency(preferences.defaultCurrency as SupportedCurrency);
    }
  }, [preferences?.defaultCurrency]);

  // Current month state for history
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Action menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Edit modal state
  const [editingEntry, setEditingEntry] = useState<ExpenseEntryRecord | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Delete dialog state
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStore();

  // Expense entries hook - pass defaultCurrency to get total in user's preferred currency
  // Amounts are stored in BRL and converted using currencySnapshots from each entry
  const { entries, total, isLoading } = useExpenseEntries(currentYear, currentMonth, defaultCurrency);
  const updateEntryMutation = useUpdateExpenseEntry();
  const deleteEntryMutation = useDeleteExpenseEntry();

  // Map nameKey to actual category UUID from server
  const categoryIdMap = useMemo(() => {
    const map = new Map<string, string>();
    if (preferences?.budgetConfig?.categories) {
      preferences.budgetConfig.categories.forEach((cat: { id: string; nameKey: string }) => {
        map.set(cat.nameKey, cat.id);
      });
    }
    return map;
  }, [preferences]);

  // Mutation for creating transaction on backend
  const createTransactionMutation = useMutation({
    mutationFn: transactionApi.createTransaction,
    onSuccess: () => {
      // Invalidate queries to refresh expense data
      queryClient.invalidateQueries({ queryKey: monthlyExpensesKeys.all });
      queryClient.invalidateQueries({ queryKey: ['yearlyReport'] });
      queryClient.invalidateQueries({ queryKey: ['expenseEntries'] });
    },
  });

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseFloat(raw) / 100 || 0;
    setAmount(numValue);
    setDisplayAmount(formatCurrency(numValue));
  };

  const handleSave = async () => {
    if (!selectedCategory || amount <= 0) return;

    // Add to local store
    addExpense({
      categoryId: selectedCategory,
      amount,
      description: description || 'Despesa',
      date: new Date().toISOString(),
    });

    // If authenticated, also save to backend
    if (isAuthenticated) {
      // Map the frontend category ID (nameKey) to the actual database UUID
      const backendCategoryId = categoryIdMap.get(selectedCategory) || selectedCategory;

      try {
        await createTransactionMutation.mutateAsync({
          amount,
          description: description || 'Despesa',
          type: 'EXPENSE',
          categoryId: backendCategoryId,
          date: new Date().toISOString(),
          currency: selectedCurrency,
        });
      } catch (error) {
        addToast('error', 'Erro ao salvar no servidor');
        console.error('Failed to save transaction:', error);
      }
    }

    // Reset form
    setAmount(0);
    setDisplayAmount('0,00');
    setSelectedCategory(null);
    setDescription('');
    setShowSuccess(true);

    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleEditClick = (entry: ExpenseEntryRecord) => {
    setEditingEntry(entry);
    setIsEditModalOpen(true);
    setOpenMenuId(null);
  };

  const handleDeleteClick = (entryId: string) => {
    setDeletingEntryId(entryId);
    setIsDeleteDialogOpen(true);
    setOpenMenuId(null);
  };

  const handleEditSave = (data: { amount: number; description: string; date: string; categoryId: string }) => {
    if (!editingEntry) return;

    updateEntryMutation.mutate(
      {
        entryId: editingEntry.id,
        data,
      },
      {
        onSuccess: () => {
          setIsEditModalOpen(false);
          setEditingEntry(null);
        },
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (!deletingEntryId) return;

    deleteEntryMutation.mutate(deletingEntryId, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setDeletingEntryId(null);
      },
    });
  };

  const isSaving = createTransactionMutation.isPending;

  const isValid = selectedCategory && amount > 0;

  return (
    <div className="px-4 py-6 space-y-8">
      {/* Total Card */}
      <div className={`rounded-2xl p-4 ${isDark ? 'bg-red-900/20 border border-red-800/50' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex items-center gap-3">
          {/* Dollar Icon */}
          <div className={`w-12 h-12 rounded-full flex items-center justify-center animate-single-flip ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
            <svg className={`w-6 h-6 ${isDark ? 'text-red-400' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <span className={`text-sm font-medium ${isDark ? 'text-red-300' : 'text-red-700'}`}>
            {t('pages.budget.monthTotal')}
          </span>
          <span
            className={`text-xl font-bold ml-auto ${isDark ? 'text-red-400' : 'text-red-600'}`}
            data-testid="expenses-total"
          >
            {CURRENCY_SYMBOLS[defaultCurrency]} {formatCurrency(total)}
          </span>
        </div>
      </div>
      {/* Header */}
      <div className="text-center">
        <h2 className={`text-xl font-semibold mb-1 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
          {t('pages.budget.addExpense')}
        </h2>
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t('pages.budget.expenseValue')}</p>
      </div>

      {/* Amount Input */}
      <div className="text-center">
        <div className="inline-flex items-baseline gap-1">
          <CurrencyButton
            currency={selectedCurrency}
            onCurrencyChange={setSelectedCurrency}
            size="md"
          />
          <input
            type="text"
            inputMode="decimal"
            value={displayAmount}
            onChange={handleAmountChange}
            data-testid="expense-amount-input"
            autoFocus
            className="text-5xl font-bold text-primary-500 bg-transparent border-none outline-none text-center w-48"
            placeholder="0,00"
          />
        </div>
      </div>

      {/* Description Input */}
      <div>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('pages.budget.descriptionOptional')}
          data-testid="expense-description-input"
          className="input text-center"
        />
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-2 gap-3" data-testid="category-grid">
        {categories.map((category) => (
          <CategoryButton
            key={category.id}
            category={category}
            isSelected={selectedCategory === category.id}
            onClick={() => setSelectedCategory(category.id)}
            isDark={isDark}
          />
        ))}
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={!isValid || isSaving}
        data-testid="add-expense-button"
        className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
          isValid
            ? 'bg-primary-500 hover:bg-primary-600 text-white'
            : isDark
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
        }`}
      >
        {isSaving ? t('common.saving') : t('common.add')}
      </button>

      {/* Success Message */}
      {showSuccess && (
        <div className="fixed bottom-24 left-4 right-4 bg-green-900/90 border border-green-700 text-green-100 px-4 py-3 rounded-lg text-center animate-slide-up">
          {t('pages.budget.addSuccess')}
        </div>
      )}

      {/* History Section */}
      <div className="pt-4 border-t border-slate-700/30">
        <h2 className={`text-lg font-semibold mb-3 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
          {t('pages.budget.history')} ({monthNames[currentMonth - 1]})
        </h2>
        {isLoading ? (
          <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('common.loading')}
          </div>
        ) : entries.length === 0 ? (
          <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {t('pages.budget.noExpenses')}
          </div>
        ) : (
          <div className="space-y-2" data-testid="expenses-list">
            {entries.map((entry) => (
              <div
                key={entry.id}
                data-testid={`expense-item-${entry.id}`}
                className={`flex items-center justify-between p-3 rounded-lg relative ${
                  isDark ? 'bg-slate-800/50' : 'bg-white border border-slate-200'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p
                      className={`font-medium truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}
                      data-testid={`expense-description-${entry.id}`}
                    >
                      {entry.description}
                    </p>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: entry.categoryColor + '20', color: entry.categoryColor }}
                    >
                      {getCategoryName(t, entry.categoryNameKey, entry.categoryName)}
                    </span>
                  </div>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {formatDate(entry.date)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}
                    data-testid={`expense-amount-${entry.id}`}
                  >
                    {CURRENCY_SYMBOLS[entry.currency as SupportedCurrency] || 'R$'} {formatCurrency(getOriginalAmount(entry.amount, entry.currency, entry.currencySnapshots))}
                  </span>
                  {/* Action Menu Button */}
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === entry.id ? null : entry.id)}
                      className={`p-1.5 rounded-full transition-colors ${
                        isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                      }`}
                      data-testid={`expense-action-menu-button-${entry.id}`}
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="6" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="18" r="1.5" />
                      </svg>
                    </button>
                    <ActionMenu
                      isOpen={openMenuId === entry.id}
                      onClose={() => setOpenMenuId(null)}
                      onEdit={() => handleEditClick(entry)}
                      onDelete={() => handleDeleteClick(entry.id)}
                      isDark={isDark}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <EditExpenseModal
        entry={editingEntry}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingEntry(null);
        }}
        onSave={handleEditSave}
        isPending={updateEntryMutation.isPending}
        categoryIdMap={categoryIdMap}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setDeletingEntryId(null);
        }}
        onConfirm={handleDeleteConfirm}
        isPending={deleteEntryMutation.isPending}
        title={t('modals.deleteConfirm.expenseTitle')}
        message={t('modals.deleteConfirm.expenseMessage')}
      />
    </div>
  );
}
